import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { brandAssets, brandCopy } from "@/constants/brand";

const features = [
  {
    title: "Shared Battle Maps",
    description: "Summon cinematic maps, shift fog, and lead your table through ruined kingdoms.",
  },
  {
    title: "Covenant Chat",
    description: "Speak in-channel, pin lore, and keep the table united under a single banner.",
  },
  {
    title: "Crimson Dice",
    description: "Roll ritual dice expressions with instant, dramatic results logged forever.",
  },
  {
    title: "Living Character Sheets",
    description: "Track stats, levels, and personal lore with parchment-styled sheets.",
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0c0a09] text-stone-100" data-testid="landing-page">
      <section className="relative min-h-screen flex flex-col" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${brandAssets.hero})` }}
          data-testid="hero-background"
        />
        <div className="absolute inset-0 hero-overlay" data-testid="hero-overlay" />
        <nav
          className="relative z-10 flex flex-wrap items-center justify-between px-6 py-6 max-w-6xl mx-auto w-full"
          data-testid="landing-navigation"
        >
          <div className="flex items-center gap-4" data-testid="landing-logo">
            <img
              src={brandAssets.logo}
              alt="Dracon Gaming"
              className="h-14 w-14 rounded-full border border-red-900/50 object-cover"
              data-testid="landing-logo-image"
            />
            <div>
              <p className="font-cinzel text-xl" data-testid="landing-brand-title">
                {brandCopy.headline}
              </p>
              <p
                className="text-xs uppercase tracking-[0.4em] text-stone-400"
                data-testid="landing-brand-subtitle"
              >
                Dracon Sanctum
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4" data-testid="landing-nav-actions">
            <Link to="/auth" data-testid="landing-signin-link">
              <Button
                variant="ghost"
                className="text-stone-200 hover:text-stone-100"
                data-testid="landing-signin-button"
              >
                Enter
              </Button>
            </Link>
            <Link to="/auth" data-testid="landing-cta-link">
              <Button
                className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em] button-jewel"
                data-testid="landing-cta-button"
              >
                Summon a Room
              </Button>
            </Link>
          </div>
        </nav>
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 flex-1 flex flex-col justify-center">
          <p
            className="text-xs uppercase tracking-[0.4em] text-stone-400"
            data-testid="landing-hero-eyebrow"
          >
            Dark Dystopian Virtual Tabletop
          </p>
          <h1
            className="font-cinzel text-4xl md:text-6xl text-stone-100 mt-6 dragon-glow"
            data-testid="landing-hero-title"
          >
            Command the Dracon Sanctum.
          </h1>
          <p className="max-w-2xl text-lg text-stone-200 mt-6" data-testid="landing-hero-subtitle">
            {brandCopy.subheadline} {brandCopy.tagline}
          </p>
          <div className="flex flex-wrap gap-4 mt-10" data-testid="landing-hero-actions">
            <Link to="/auth" data-testid="landing-primary-cta-link">
              <Button
                className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em] button-jewel"
                data-testid="landing-primary-cta"
              >
                Start a Campaign
              </Button>
            </Link>
            <Link to="/dashboard" data-testid="landing-secondary-cta-link">
              <Button
                variant="secondary"
                className="border border-stone-700 text-stone-200 hover:text-stone-100"
                data-testid="landing-secondary-cta"
              >
                View War Rooms
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="features-section">
        <div className="flex flex-col gap-4 mb-12">
          <p
            className="text-xs uppercase tracking-[0.4em] text-stone-500"
            data-testid="features-eyebrow"
          >
            Core Arsenal
          </p>
          <h2 className="font-cinzel text-3xl" data-testid="features-title">
            Everything your table needs to thrive.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="features-grid">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="panel-iron p-8 rounded-xl"
              data-testid={`feature-card-${index}`}
            >
              <h3 className="font-cinzel text-xl" data-testid={`feature-title-${index}`}>
                {feature.title}
              </h3>
              <p className="text-stone-300 mt-4" data-testid={`feature-description-${index}`}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24" data-testid="cta-section">
        <div className="panel-glass rounded-2xl p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <p
              className="text-xs uppercase tracking-[0.4em] text-stone-400"
              data-testid="cta-eyebrow"
            >
              Ready for the ritual?
            </p>
            <h3 className="font-cinzel text-2xl mt-4" data-testid="cta-title">
              Invite your party and open the gates.
            </h3>
          </div>
          <Link to="/auth" data-testid="cta-action-link">
            <Button
              className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em] button-jewel"
              data-testid="cta-action-button"
            >
              Enter the Sanctum
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
